/* ============================================================
   Site footer.

   Four columns and two thin bands: brand and who-we-are, the tool
   columns split by track, a reference column, contact and legal — then
   the sister-practice line and the copyright.

   It used to be four columns and four bands, and the extra two were
   both restatements. A "learn the ground" card strip listed four
   references, two of which the Move column already carried as the tools
   they are; and the copyright line repeated Privacy, Terms, Method and
   About as single words, all four already linked in full further up.
   Every reference the reader needs is still here, once each.

   Two details worth keeping from the sister JK & Associates site:

   · Never link a page to itself. A self-referential footer link reads
     as a broken one, so the current route renders as plain text.
   · A copy-email control alongside the mailto. On a device with no
     configured mail client a mailto link silently does nothing, which
     looks like a broken site rather than a missing app.

   Tool columns are generated from the registry, so a tool cannot appear
   in the footer without a route — the same guarantee the homepage has.
   ============================================================ */

import { SITE, ABOUT, LEARN_LINKS, LEGAL_LINKS } from '../data/site.js';
import { TRACKS, toolsInTrack } from '../data/tools.js';
import { getBrand, isWhiteLabel, isDemonstration } from '../shared/brand.js';
import { router } from '../shared/router.js';
import { html, raw } from '../shared/html.js';
import { icon } from '../shared/icons.js';
import { toast } from '../shared/toast.js';
import { mark } from './Logo.js';

export function Footer() {
  const footer = document.createElement('footer');
  footer.className = 'footer no-print';

  const paint = () => {
    const brand = getBrand();
    const here = router.path;

    const listItem = (path, label) => html`<li>
      ${here === path
        ? html`<span aria-current="page">${label}</span>`
        : html`<a href="${path}">${label}</a>`}
    </li>`;

    const column = (track) => {
      const tools = toolsInTrack(track.id).filter((t) => t.status === 'live');
      return html`
        <div>
          <h2>${track.label}</h2>
          <ul>
            ${tools.map((tool) => listItem(tool.path, tool.title))}
          </ul>
        </div>
      `;
    };

    /* The reference links, as a third column beside Move and Build —
       which is how the nav has always grouped them, and the footer is
       the same map.

       They used to be a separate "Learn the ground" card band lower
       down, and two of the four were already listed one column over:
       Glossary and the Compliance Calendar are registered tools on the
       Move track, so the footer offered them twice, once as a tool and
       once as a card. Filtering against the live registry rather than
       trimming the list by hand means the overlap cannot come back — if
       Glossary ever stops being a tool it reappears here on its own. */
    const columnPaths = new Set(
      Object.values(TRACKS).flatMap((t) => toolsInTrack(t.id).filter((x) => x.status === 'live').map((x) => x.path))
    );
    const learnElsewhere = LEARN_LINKS.filter((l) => !columnPaths.has(l.path));

    footer.innerHTML = html`
      <div class="container">
        <div class="footer-cols">
          <div class="footer-brand">
            <span class="footer-mark">${raw(mark({ height: 26, mono: 'currentColor' }))}</span>
            <div>
              <div class="footer-name">${brand.shortName}</div>
              <div class="footer-descriptor">${brand.descriptor || SITE.descriptor}</div>
            </div>
            <p>${SITE.blurb}</p>
            ${isWhiteLabel()
              ? html`<p class="footer-cobrand">Powered by ${SITE.name}, for ${brand.name}.</p>`
              : ''}
            ${isDemonstration()
              ? html`<p class="footer-demo-note">
                  This is a demonstration of how ${SITE.name} looks under a
                  partner's identity. ${brand.shortName} is not affiliated with
                  ${SITE.name} and has not endorsed this tool.
                </p>`
              : ''}

            <!-- Who the practice is, in the brand column rather than in
                 a band of its own. It used to sit full-width below the
                 columns and use about half that width, under a column
                 that had 150px of empty space in it — a band and a void,
                 for one paragraph. Here it fills the void and the band
                 goes away. -->
            <section class="footer-about">
              <h2>${ABOUT.heading}</h2>
              <p>${ABOUT.lead} ${ABOUT.leadMore}</p>
              ${here === ABOUT.path
                ? html`<span class="footer-about-more" aria-current="page">${ABOUT.linkLabel}</span>`
                : html`<a class="footer-about-more" href="${ABOUT.path}">${ABOUT.linkLabel} →</a>`}
            </section>
          </div>

          ${Object.values(TRACKS).map(column)}

          <div>
            ${learnElsewhere.length
              ? html`<h2>Learn</h2>
                  <ul>
                    ${learnElsewhere.map((l) => listItem(l.path, l.label))}
                  </ul>
                  <h2 style="margin-top: var(--ca-space-5);">Contact</h2>`
              : html`<h2>Contact</h2>`}
            <ul>
              <li><a href="mailto:${SITE.email}">${SITE.email}</a></li>
              <li>
                <button class="footer-copy" id="copy-email" type="button">Copy address</button>
              </li>
              <li>${SITE.location}</li>
            </ul>
            <h2 style="margin-top: var(--ca-space-5);">Legal</h2>
            <ul>
              ${LEGAL_LINKS.map(
                (l) =>
                  html`<li>
                    ${here === l.path
                      ? html`<span aria-current="page">${l.label}</span>`
                      : html`<a href="${l.path}">${l.label}</a>`}
                  </li>`
              )}
            </ul>
          </div>
        </div>

        <div class="footer-sister">
          ${raw(icon('globe', 14))}
          <span>
            Sister practice:
            <a href="${SITE.sister.url}" target="_blank" rel="noopener noreferrer">
              ${SITE.sister.name}
            </a>
            — ${SITE.sister.descriptor}. ${SITE.sister.blurb}
          </span>
        </div>

        <div class="footer-legal">
          <span>
            © <span class="num" id="footer-year"></span> ${SITE.publishedName} ·
            ${SITE.tagline}.
          </span>
          <!-- Just the version. This line used to repeat Privacy, Terms,
               Method and About as single words, all four of which are
               already linked in full a few hundred pixels above — the
               legal column carries Privacy and Terms, the Learn column
               carries Method, and the About band ends on its own link.
               A second, abbreviated copy of a link the reader has just
               scrolled past is not a convenience. -->
          <span class="footer-version">${SITE.version}</span>
        </div>
      </div>
    `;

    // Dynamic, so a stale build never shows last year's copyright.
    footer.querySelector('#footer-year').textContent = String(new Date().getFullYear());

    footer.querySelector('#copy-email').addEventListener('click', async (event) => {
      const button = event.currentTarget;
      const original = button.textContent;
      try {
        await navigator.clipboard.writeText(SITE.email);
        button.textContent = 'Copied ✓';
        toast('Email address copied');
      } catch {
        // Clipboard needs a secure context and permission. Show the
        // address rather than failing silently.
        button.textContent = SITE.email;
      }
      setTimeout(() => {
        button.textContent = original;
      }, 1800);
    });
  };

  paint();
  window.addEventListener('routechange', paint);
  window.addEventListener('brandchange', paint);

  return footer;
}
